/**
 * Michigan-specific tax structure utilities.
 * 
 * Michigan uses Summer/Winter tax periods instead of 1st Half/2nd Half.
 * This module centralizes all MI-specific display logic so UI, notes, and PDF stay consistent.
 * 
 * Usage:
 *   import { isMichigan, getMichiganTaxDisplayLabel, MI_SEASONS } from '../utils/michiganTax';
 */

/**
 * Check if a state code is Michigan
 */
export const isMichigan = (stateCode) => {
  if (!stateCode) return false;
  const normalized = stateCode.trim().toUpperCase();
  return normalized === 'MI' || normalized === 'MICHIGAN';
};

/**
 * Available seasons for MI tax entries
 */
export const MI_SEASONS = [
  { value: 'SUMMER', label: 'SUMMER' },
  { value: 'WINTER', label: 'WINTER' },
];

/**
 * Feb 2026 — Per-frequency MI Season options.
 *
 * Some MI jurisdictions (e.g. Village of Capac) collect annually with no
 * Summer / Winter distinction. When Tax Collected = Annual, the dropdown
 * exposes an extra `Annual` option so processors can label the row
 * accurately. Auto-generation defaults stay SUMMER (see server.py
 * create_default_installments), so existing in-progress MI annual orders
 * are unaffected — users opt in explicitly.
 *
 * Non-annual frequencies (semi-annually, quarterly) are unchanged.
 *
 * @param {string} taxCollected - 'annually' | 'semi_annually' | 'quarterly'
 * @returns {Array<{value: string, label: string}>}
 */
export const getMiAvailableSeasons = (taxCollected) => {
  const freq = (taxCollected || '').trim().toLowerCase();
  if (freq === 'annually') {
    return [
      // Feb 2026 — Label uppercased to 'ANNUAL' to match the
      // SUMMER / WINTER casing convention used everywhere else in
      // the MI Season workflow (dropdown trigger, fulfillment
      // badges, PDF Year column). The stored `value` was already
      // 'ANNUAL', so this is a UI-only consistency tweak — no
      // backend, schema, or notes-wording change.
      { value: 'ANNUAL', label: 'ANNUAL' },
      ...MI_SEASONS,
    ];
  }
  return MI_SEASONS;
};

/**
 * Get default installment labels for Michigan based on frequency
 */
export const getMiDefaultLabels = (taxCollected) => {
  switch (taxCollected) {
    case 'semi_annually': return ['SUMMER', 'WINTER'];
    case 'annually': return ['SUMMER'];
    case 'quarterly': return ['SUMMER-1', 'SUMMER-2', 'WINTER-1', 'WINTER-2'];
    default: return ['SUMMER', 'WINTER'];
  }
};

/**
 * Resolve the display label for a Michigan tax installment.
 * 
 * THE centralized label resolver. Used everywhere:
 * - Tax Summary table labels
 * - Notes text
 * - PDF summary labels
 * - Review screen
 * 
 * @param {Object} record - The installment record (must have 'season' for MI)
 * @param {Array} siblingRecords - All installments for same tax year + jurisdiction
 * @param {string} stateCode - State code (only applies MI logic when 'MI')
 * @returns {string} Display label: "SUMMER", "SUMMER-1", "WINTER", "WINTER-2", etc.
 */
export const getMichiganTaxDisplayLabel = (record, siblingRecords = [], stateCode = '') => {
  if (!isMichigan(stateCode)) {
    return record.installment_name || `Installment ${record.installment_index || 1}`;
  }

  let season = (record.season || '').trim().toUpperCase();
  if (!season) {
    // Fallback: try to infer from installment_name
    const name = (record.installment_name || '').toUpperCase();
    if (name.includes('SUMMER')) season = 'SUMMER';
    else if (name.includes('WINTER')) season = 'WINTER';
    else return record.installment_name || 'Unknown';
  }

  if (!siblingRecords || siblingRecords.length === 0) return season;

  // Count siblings with the same season
  const sameSeason = siblingRecords.filter(
    r => (r.season || '').trim().toUpperCase() === season
  );

  if (sameSeason.length <= 1) return season;

  // Multiple records for same season — add numbering
  const sorted = [...sameSeason].sort(
    (a, b) => (a.installment_index || 1) - (b.installment_index || 1)
  );
  const idx = sorted.findIndex(r => r.id === record.id);
  if (idx === -1) return season;

  if (season === 'WINTER' && idx === 0) return 'WINTER';
  if (season === 'WINTER') return `WINTER-${idx + 1}`;
  // Feb 2026 — defensive numbering for multi-ANNUAL rows (rare; the
  // consolidator flags multi-ANNUAL as specialty so this is mostly a
  // safety net for legacy data).
  if (season === 'ANNUAL' && idx === 0) return 'ANNUAL';
  if (season === 'ANNUAL') return `ANNUAL-${idx + 1}`;
  return `SUMMER-${idx + 1}`;
};

const _numToWord = (n) => {
  const w = {1:'one',2:'two',3:'three',4:'four',5:'five',6:'six',7:'seven',8:'eight',9:'nine',10:'ten'};
  return w[n] || String(n);
};

// ─────────────────────────────────────────────────────────────────────
// Jun 2026 — Phase 20 (UAT-MI-FULLY-EXEMPT-NOTES).
// Michigan parity for Fully Exempt narrative generation.
//
// Florida (and every other non-MI state) emits the
// "qualifies for a 100% <ExemptionLabel> exemption; therefore,
//  the <type> tax amounts are zero." sentence when
//  jurisdiction.fully_exempt is true (JurisdictionManager.jsx L2094).
// The MI branch (L2028) returns early BEFORE that block ever runs,
// so MI parcels marked Fully Exempt produced a narrative that
// completely omitted the exemption language — Ops had to paste it
// into Additional Notes manually.
//
// This builder reuses the Florida wording shape, swapping the
// "first-half / second-half" date phrasing for Michigan's
// Summer / Winter / Annual cycle. Multi-year exemption uses the
// generic "applicable delinquency dates" closer, identical to the
// Florida multi-year branch (L2204).
// ─────────────────────────────────────────────────────────────────────
const _fmtDateMi = (dateVal) => {
  if (!dateVal) return null;
  if (typeof dateVal === 'string' && dateVal.includes('/')) return dateVal;
  if (typeof dateVal === 'string' && dateVal.includes('-')) {
    const ps = dateVal.split('-');
    if (ps.length === 3) return `${ps[1]}/${ps[2]}/${ps[0]}`;
  }
  return dateVal;
};

const _joinAnd = (arr) => {
  if (arr.length === 0) return '';
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
  return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
};

const _resolveExemptionLabels = (exemptionTypes, exemptionOther) => {
  const cleaned = (exemptionTypes || []).filter(
    (t) => t && t !== 'No Exemptions (N/A)'
  );
  const labels = cleaned
    .map((t) => {
      const trimmed = (t || '').trim();
      const lower = trimmed.toLowerCase();
      if (lower === 'other' || lower === 'other (custom)') {
        const custom = (exemptionOther || '').trim();
        return custom || 'Other';
      }
      return trimmed;
    })
    .map((s) => s.replace(/ exemptions?$/i, '').trim())
    .filter(Boolean);
  return labels;
};

/**
 * Build the Michigan Fully Exempt narrative for one tax year.
 *
 * Returns a single narrative string OR null when called for a
 * non-exempt jurisdiction. Caller must short-circuit BEFORE the
 * normal MI narrative runs (the exempt path replaces it wholesale,
 * matching Florida's "return early" behaviour at JurisdictionManager
 * L2231).
 */
export const buildMichiganFullyExemptNarrative = (installments, taxYear, options = {}) => {
  const {
    exemptionTypes = [],
    exemptionOther = '',
    allYearsInstallments = null,
  } = options;

  const labels = _resolveExemptionLabels(exemptionTypes, exemptionOther);
  const exemptionLabel = _joinAnd(labels) || 'tax';
  const exemptionNoun = labels.length > 1 ? 'exemptions' : 'exemption';
  const exemptionArticle = labels.length > 1 ? '' : 'a ';

  // ── Multi-year detection (mirrors FL FULLY-EXEMPT-MULTI-YEAR-NOTE) ──
  const yearsAll = Array.from(new Set(
    (installments || []).map((i) => (i?.tax_year ?? '').toString().trim()).filter(Boolean)
  )).sort((a, b) => Number(a) - Number(b));
  const yearJoined = _joinAnd(yearsAll);
  const yearText = yearJoined ? `${yearJoined} ` : (taxYear ? `${taxYear} ` : '');
  const isMultiYear = yearsAll.length > 1;

  // ── Prior-tax prefix (matches FL `priorTaxPrefix` logic) ──
  const hasDelinquent = (installments || []).some((inst) =>
    ['delinquent', 'partially delinquent', 'partially dlq'].includes((inst.pay_status || '').toLowerCase())
  );
  const priorPrefix = !hasDelinquent ? 'All prior taxes are paid. ' : '';

  // ── Season bucketing ──
  const rows = installments || [];
  const summer = rows.filter((r) => (r.season || '').toUpperCase() === 'SUMMER');
  const winter = rows.filter((r) => (r.season || '').toUpperCase() === 'WINTER');
  const annual = rows.filter((r) => (r.season || '').toUpperCase() === 'ANNUAL');

  // ── Season qualifier in the "tax amounts are zero" sentence ──
  // Mirrors FL "the <type> tax amounts are zero" pattern. For MI we
  // qualify by season so the narrative reads naturally:
  //   • Summer + Winter → "the Summer and Winter tax amounts are zero"
  //   • Summer only     → "the Summer tax amounts are zero"
  //   • Winter only     → "the Winter tax amounts are zero"
  //   • Annual          → "the Annual tax amount is zero"
  //   • Unknown / none  → "the tax amounts are zero"
  let seasonClause;
  let amountClause;
  if (summer.length > 0 && winter.length > 0) {
    seasonClause = 'Summer and Winter ';
    amountClause = 'amounts are zero';
  } else if (summer.length > 0) {
    seasonClause = 'Summer ';
    amountClause = 'amounts are zero';
  } else if (winter.length > 0) {
    seasonClause = 'Winter ';
    amountClause = 'amounts are zero';
  } else if (annual.length > 0) {
    seasonClause = 'Annual ';
    amountClause = 'amount is zero';
  } else {
    seasonClause = '';
    amountClause = 'amounts are zero';
  }

  let exemptNote = (
    `For ${yearText}taxes, the property qualifies for ${exemptionArticle}100% `
    + `${exemptionLabel} ${exemptionNoun}; therefore, the ${seasonClause}tax ${amountClause}.`
  );

  // ── Due / delinquency dates ──
  if (isMultiYear) {
    exemptNote += ' Taxes become delinquent after the applicable delinquency dates.';
    return priorPrefix + exemptNote;
  }

  const dateParts = [];
  if (summer.length > 0) {
    const due = _fmtDateMi((summer[0].due_date || '').trim());
    const dlq = _fmtDateMi((summer[0].dlq_date || '').trim());
    if (due) {
      let part = `Summer taxes were due on ${due}`;
      if (dlq) part += ` and become delinquent after ${dlq}`;
      dateParts.push(part);
    }
  }
  if (winter.length > 0) {
    const due = _fmtDateMi((winter[0].due_date || '').trim());
    const dlq = _fmtDateMi((winter[0].dlq_date || '').trim());
    if (due) {
      let part = `Winter taxes were due on ${due}`;
      if (dlq) part += ` and become delinquent after ${dlq}`;
      dateParts.push(part);
    }
  }
  if (annual.length > 0 && summer.length === 0 && winter.length === 0) {
    const due = _fmtDateMi((annual[0].due_date || '').trim());
    const dlq = _fmtDateMi((annual[0].dlq_date || '').trim());
    if (due) {
      let part = `The due date is ${due}`;
      if (dlq) part += ` and taxes become delinquent after ${dlq}`;
      dateParts.push(part);
    }
  }
  if (dateParts.length > 0) {
    exemptNote += ' ' + dateParts.join('. ') + '.';
  }

  // `allYearsInstallments` accepted for API symmetry with
  // getMichiganNotesText; currently unused by the exempt builder
  // (prior-prefix is computed from `installments` directly).
  void allYearsInstallments;

  return priorPrefix + exemptNote;
};

/**
 * Generate Michigan-specific notes as a single client-friendly narrative paragraph.
 * 
 * @param {Array} installments - All installments for one jurisdiction + year
 * @param {string} taxYear - The tax year string
 * @param {Object} options - Additional options
 * @param {string} options.taxCollected - Collection frequency ("semi_annually", "annually", etc.)
 * @param {Array} options.allYearsInstallments - ALL installments across ALL years for this jurisdiction
 * @param {string} options.jurisdictionType - Jurisdiction type for clean wording
 * @returns {Array<string>} Single-element array with the narrative paragraph
 */
export const getMichiganNotesText = (installments, taxYear, options = {}) => {
  const parts = [];
  const {
    taxCollected = '',
    allYearsInstallments = null,
    jurisdictionType = '',
    // Jun 2026 — Phase 20 (UAT-MI-FULLY-EXEMPT-NOTES). Exemption
    // context plumbed from JurisdictionManager so the MI branch
    // emits the same exemption sentence Florida already does. When
    // `fullyExempt` is true we short-circuit to the dedicated
    // narrative builder and skip the standard status / dates path.
    fullyExempt = false,
    exemptionTypes = [],
    exemptionOther = '',
  } = options;

  // ── Fully Exempt short-circuit (parity with FL JurisdictionManager L2094) ──
  if (fullyExempt) {
    return [
      buildMichiganFullyExemptNarrative(installments, taxYear, {
        exemptionTypes,
        exemptionOther,
        allYearsInstallments,
      }),
    ];
  }
  
  // ── 1. Prior taxes ──
  if (allYearsInstallments && taxYear) {
    const currentYr = parseInt(taxYear, 10);
    if (!isNaN(currentYr)) {
      const priorRows = allYearsInstallments.filter(r => {
        const yr = parseInt(r.tax_year, 10);
        return !isNaN(yr) && yr < currentYr;
      });
      if (priorRows.length > 0) {
        if (priorRows.every(r => (r.pay_status || 'Due').toLowerCase() === 'paid')) {
          parts.push('All prior taxes are paid.');
        }
      } else {
        parts.push('All prior taxes are paid.');
      }
    }
  }
  
  const summer = installments.filter(i => (i.season || '').toUpperCase() === 'SUMMER');
  const winter = installments.filter(i => (i.season || '').toUpperCase() === 'WINTER');
  // Feb 2026 — `ANNUAL` season rows (MI annual jurisdictions like
  // Village of Capac that have no Summer / Winter distinction).
  // Strictly additive: Summer + Winter buckets remain untouched.
  const annual = installments.filter(i => (i.season || '').toUpperCase() === 'ANNUAL');

  // ── 2. Collection frequency ──
  const freq = (taxCollected || '').trim().toLowerCase();
  if (freq === 'semi_annually' || freq === 'semi-annually') {
    parts.push('Taxes are collected semi-annually.');
  } else if (freq === 'annually') {
    parts.push('Taxes are collected annually.');
  } else if (freq === 'quarterly') {
    parts.push('Taxes are collected quarterly.');
  } else if (summer.length > 0 && winter.length > 0) {
    parts.push('Taxes are collected semi-annually.');
  }

  // ── 3. Season status (combined sentence) ──
  const statusPhrase = (seasonName, rows) => {
    if (!rows.length) return null;
    const statuses = rows.map(r => (r.pay_status || 'Due').toLowerCase());
    const dpValues = rows.map(r => parseFloat(r.discount_penalty) || 0);
    const allPaid = statuses.every(s => s === 'paid');
    const allDue = statuses.every(s => s === 'due');
    const allDlq = statuses.every(s => s.includes('delinquent') || s.includes('dlq'));
    const hasPositiveAdj = dpValues.some(dp => dp > 0);
    const hasDiscount = dpValues.some(dp => dp < 0);
    const multi = rows.length > 1;
    const instNote = multi ? ` in ${_numToWord(rows.length)} installments` : '';
    const prefix = `${taxYear} ${seasonName}`;
    
    // Determine if positive adjustment is a "fee" or "penalty" based on payment timing
    let adjLabel = 'penalty';
    if (hasPositiveAdj) {
      const adjRow = rows.find(r => (parseFloat(r.discount_penalty) || 0) > 0);
      if (adjRow) {
        const paidDate = adjRow.paid_date ? new Date(adjRow.paid_date) : null;
        const dueDate = adjRow.due_date ? new Date(adjRow.due_date) : null;
        if (paidDate && dueDate && paidDate <= dueDate) {
          adjLabel = 'fee';
        }
      }
    }

    // Feb 2026 — BILLING/CERT P0: penalty determination is money-based
    // only. Detect implicit penalty (paid_amount > base_tax); do NOT
    // infer penalty purely from paid_date > dlq_date.
    let paidAfterDlq = false;
    if (allPaid && !hasPositiveAdj) {
      for (const r of rows) {
        const pa = parseFloat(r.paid_amount) || 0;
        const bt = parseFloat(r.base_tax) || 0;
        if (pa - bt > 0.01) { paidAfterDlq = true; break; }
      }
    }

    if (allPaid) {
      if (hasPositiveAdj) {
        if (adjLabel === 'fee') {
          const feeAmt = dpValues.reduce((sum, dp) => sum + (dp > 0 ? dp : 0), 0);
          return `${prefix} taxes are paid in full, with an additional fee of $${feeAmt.toLocaleString('en-US', {minimumFractionDigits: 2})}${instNote}`;
        }
        return `${prefix} taxes are paid with penalty${instNote}`;
      }
      if (paidAfterDlq) return `${prefix} taxes are paid with penalty${instNote}`;
      if (hasDiscount) return `${prefix} taxes are paid at a discount${instNote}`;
      return `${prefix} taxes are paid${instNote}`;
    }
    if (allDue) return `${prefix} taxes are due${instNote}`;
    if (allDlq) return `${prefix} taxes are delinquent`;
    const mixed = rows.map(r => {
      const label = getMichiganTaxDisplayLabel(r, rows, 'MI');
      return `${label} is ${(r.pay_status || 'Due').toLowerCase()}`;
    });
    return `${prefix} taxes: ${mixed.join(', ')}`;
  };

  const summerPhrase = statusPhrase('Summer', summer);
  const winterPhrase = statusPhrase('Winter', winter);
  // Feb 2026 — Annual phrase, emitted only when ANNUAL-season rows
  // exist. Stays parallel to the Summer/Winter sentences.
  const annualPhrase = statusPhrase('Annual', annual);
  if (summerPhrase && winterPhrase) {
    parts.push(`${summerPhrase}, and ${winterPhrase}.`);
  } else if (summerPhrase) {
    parts.push(`${summerPhrase}.`);
  } else if (winterPhrase) {
    parts.push(`${winterPhrase}.`);
  }
  if (annualPhrase) {
    parts.push(`${annualPhrase}.`);
  }

  // ── 4. Due dates + delinquency schedule ──
  const summerDue = summer.length > 0 ? (summer[0].due_date || '').trim() : '';
  const summerDlq = summer.length > 0 ? (summer[0].dlq_date || '').trim() : '';
  const winterDue = winter.length > 0 ? (winter[0].due_date || '').trim() : '';
  const winterDlq = winter.length > 0 ? (winter[0].dlq_date || '').trim() : '';
  // Feb 2026 — Annual due/dlq, same shape as Summer/Winter.
  const annualDue = annual.length > 0 ? (annual[0].due_date || '').trim() : '';
  const annualDlq = annual.length > 0 ? (annual[0].dlq_date || '').trim() : '';

  const dateClauses = [];
  if (summerDue) {
    let clause = `Summer taxes were due on ${summerDue}`;
    if (summerDlq) clause += ` and became delinquent after ${summerDlq}`;
    dateClauses.push(clause);
  }
  if (winterDue) {
    let clause = `Winter taxes were due on ${winterDue}`;
    if (winterDlq) clause += ` and became delinquent after ${winterDlq}`;
    dateClauses.push(clause);
  }
  if (dateClauses.length === 2) {
    parts.push(`${dateClauses[0]}, while ${dateClauses[1]}.`);
  } else if (dateClauses.length === 1) {
    parts.push(`${dateClauses[0]}.`);
  }
  if (annualDue) {
    let aClause = `Annual taxes were due on ${annualDue}`;
    if (annualDlq) aClause += ` and became delinquent after ${annualDlq}`;
    parts.push(`${aClause}.`);
  }

  // ── 5. Multi-installment option ──
  let jurLabel = (jurisdictionType || '').trim().toLowerCase();
  jurLabel = ['county', 'city', 'township', 'village', 'borough'].includes(jurLabel)
    ? jurLabel.charAt(0).toUpperCase() + jurLabel.slice(1) : 'jurisdiction';
  if (summer.length >= 2 && winter.length >= 1) {
    const total = summer.length + winter.length;
    parts.push(`This ${jurLabel} offers a ${_numToWord(total)}-installment payment option.`);
  }

  const paragraph = parts.join(' ');
  return paragraph ? [paragraph] : [];
};


// ===========================================================================
// Feb 26, 2026 — Multi-year notes CONSOLIDATOR (Option A wrapper, Phase 1: MI)
// ---------------------------------------------------------------------------
// Mirror of `michigan_tax.resolve_mi_notes_consolidated` (backend). Keeps the
// UI Notes Preview output identical to the PDF / Email-attachment output.
// See backend module docstring for full behavior contract.
// ===========================================================================

const _formatYearList = (years) => {
  const yrs = Array.from(new Set(years.map(y => String(y)))).sort();
  if (yrs.length === 0) return '';
  if (yrs.length === 1) return yrs[0];
  if (yrs.length === 2) return `${yrs[0]} and ${yrs[1]}`;
  return `${yrs.slice(0, -1).join(', ')}, and ${yrs[yrs.length - 1]}`;
};

const _md = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  const slashParts = s.split('/');
  if (slashParts.length === 3) return `${slashParts[0]}/${slashParts[1]}`;
  if (s.includes('-')) {
    const dashParts = s.split('-');
    if (dashParts.length === 3) return `${dashParts[1]}/${dashParts[2]}`;
  }
  return s;
};

const _statusSimple = (rows) => {
  if (!rows || !rows.length) return null;
  const statuses = rows.map(r => (r.pay_status || 'Due').toLowerCase());
  if (statuses.every(s => s === 'paid')) {
    // Feb 2026 — BILLING/CERT P0: penalty determination is money-based
    // only. Surface "paid with penalty" when any row has a positive
    // Adj Amt or paid_amount > base_tax. Never infer penalty purely
    // from paid_date > dlq_date.
    const hasPositiveAdj = rows.some(r => (parseFloat(r.discount_penalty) || 0) > 0);
    if (hasPositiveAdj) return 'paid with penalty';
    for (const r of rows) {
      const pa = parseFloat(r.paid_amount) || 0;
      const bt = parseFloat(r.base_tax) || 0;
      if (pa - bt > 0.01) return 'paid with penalty';
    }
    // Jul 2026 — DLQ-PAID-STATUS-001: collector-confirmed years whose
    // lump-sum exceeds the base taxes AND were paid after the DLQ date
    // narrate the late-payment penalty explicitly (money evidence comes
    // from the group total, honoring the money-based-penalty contract).
    if (rows.some(r => r && r.pbna_late_penalty)) {
      return 'paid, including applicable penalty and interest due to late payment';
    }
    return 'paid';
  }
  if (statuses.every(s => s === 'due')) return 'due';
  if (statuses.every(s => s.includes('delinquent') || s.includes('dlq'))) return 'delinquent';
  return 'mixed';
};

const _stripPreambles = (paragraph) => {
  if (!paragraph) return paragraph;
  let out = paragraph;
  ['All prior taxes are paid.', 'Taxes are collected semi-annually.',
   'Taxes are collected annually.', 'Taxes are collected quarterly.'].forEach(pre => {
    out = out.split(pre + ' ').join('').split(pre).join('');
  });
  // NOTES-PAYOPT-DEDUP-001 (Jul 2026): the payment-option sentence is a
  // jurisdiction-level characteristic — stripped from per-year fallback
  // output and re-emitted ONCE at the end (Step 6).
  out = out.replace(/\s*This \S+ offers a \S+-installment payment option\./g, '');
  return out.trim();
};

const _consolidateDateClause = (season, groupSigs, spanMultiYear) => {
  const seasonLower = season.toLowerCase();
  const dueKey = `${seasonLower}_due_md`;
  const dlqKey = `${seasonLower}_dlq_md`;
  const dueMd = groupSigs[0][dueKey];
  if (!dueMd) return '';

  const dlqByYear = {};
  groupSigs.forEach(sig => { if (sig[dlqKey]) dlqByYear[sig.year] = sig[dlqKey]; });
  if (Object.keys(dlqByYear).length === 0) {
    return `${season} taxes were due on ${dueMd}${spanMultiYear ? ' each year' : ''}.`;
  }

  const counts = {};
  Object.values(dlqByYear).forEach(md => { counts[md] = (counts[md] || 0) + 1; });
  const canonicalDlq = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  const exceptions = Object.entries(dlqByYear)
    .filter(([_, md]) => md !== canonicalDlq)
    .sort();

  let clause = `${season} taxes were due on ${dueMd}`;
  if (spanMultiYear) clause += ' each year';
  clause += ` and became delinquent after ${canonicalDlq}`;
  if (exceptions.length) {
    clause += ` (${exceptions.map(([y, md]) => `${md} in ${y}`).join(', ')})`;
  }
  clause += '.';
  return clause;
};

/**
 * Top-level consolidator. See backend `resolve_mi_notes_consolidated` for
 * the full behavior contract. Frontend-side equivalent used by the UI
 * Notes Preview so the preview matches the rendered PDF.
 *
 * @param {Object} byYearRows - { '2023': [rows], '2024': [rows], … }
 * @param {Array}  allYearsInstallments - flat list of every row
 * @param {Object} options - { taxCollected, jurisdictionType }
 * @returns {Array<string>} Single-element array with the consolidated paragraph
 */
export const getMichiganNotesConsolidated = (byYearRows, allYearsInstallments, options = {}) => {
  const {
    taxCollected = '',
    jurisdictionType = '',
    // Jun 2026 — Phase 20 (UAT-MI-FULLY-EXEMPT-NOTES). When the
    // jurisdiction is fully exempt, the consolidator emits ONE
    // exemption paragraph for the full year span (matches Florida's
    // multi-year exempt branch — `isMultiYearExempt` at
    // JurisdictionManager L2204).
    fullyExempt = false,
    exemptionTypes = [],
    exemptionOther = '',
  } = options;
  if (!byYearRows || Object.keys(byYearRows).length === 0) return [];

  // ── Fully Exempt short-circuit (parity with FL multi-year exempt) ──
  if (fullyExempt) {
    return [
      buildMichiganFullyExemptNarrative(allYearsInstallments || [], '', {
        exemptionTypes,
        exemptionOther,
        allYearsInstallments,
      }),
    ];
  }

  // Step 1 — per-year signatures
  const signatures = Object.keys(byYearRows).sort().map(yr => {
    const rows = byYearRows[yr];
    const summer = rows.filter(i => (i.season || '').toUpperCase() === 'SUMMER');
    const winter = rows.filter(i => (i.season || '').toUpperCase() === 'WINTER');
    // Feb 2026 — ANNUAL bucket for MI annual jurisdictions with no
    // Summer/Winter distinction (e.g. Village of Capac).
    const annual = rows.filter(i => (i.season || '').toUpperCase() === 'ANNUAL');
    const summerStatus = _statusSimple(summer);
    const winterStatus = _statusSimple(winter);
    const annualStatus = _statusSimple(annual);
    const specialty =
      summerStatus === 'mixed' ||
      winterStatus === 'mixed' ||
      annualStatus === 'mixed' ||
      summer.length > 1 ||
      annual.length > 1 ||
      [...summer, ...winter, ...annual].some(r => (parseFloat(r.discount_penalty) || 0) !== 0);
    return {
      year: String(yr),
      summerStatus, winterStatus, annualStatus,
      summer_due_md: _md(summer[0]?.due_date),
      summer_dlq_md: _md(summer[0]?.dlq_date),
      winter_due_md: _md(winter[0]?.due_date),
      winter_dlq_md: _md(winter[0]?.dlq_date),
      annual_due_md: _md(annual[0]?.due_date),
      annual_dlq_md: _md(annual[0]?.dlq_date),
      specialty, summer, winter, annual,
    };
  });

  const specialtySigs = signatures.filter(s => s.specialty);
  const standardSigs = signatures.filter(s => !s.specialty);

  const parts = [];

  // Step 2 — preambles (once per jurisdiction)
  if (allYearsInstallments && allYearsInstallments.length) {
    const allPaid = allYearsInstallments.every(r => (r.pay_status || 'Due').toLowerCase() === 'paid');
    if (allPaid) parts.push('All prior taxes are paid.');
  }

  const freq = (taxCollected || '').trim().toLowerCase();
  if (freq === 'semi_annually' || freq === 'semi-annually') {
    parts.push('Taxes are collected semi-annually.');
  } else if (freq === 'annually') {
    parts.push('Taxes are collected annually.');
  } else if (freq === 'quarterly') {
    parts.push('Taxes are collected quarterly.');
  } else if (signatures.some(s => s.summerStatus && s.winterStatus)) {
    parts.push('Taxes are collected semi-annually.');
  }

  // Step 3 — group standard years by signature key
  const groups = new Map();
  for (const sig of standardSigs) {
    // Feb 2026 — include annualStatus + annual_due_md in the key so
    // years sharing identical Annual patterns consolidate together.
    const key = JSON.stringify([
      sig.summerStatus, sig.winterStatus, sig.annualStatus,
      sig.summer_due_md, sig.winter_due_md, sig.annual_due_md,
    ]);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(sig);
  }

  // Step 4 — emit status sentences per group
  for (const [_, sigs] of groups) {
    const { summerStatus, winterStatus, annualStatus } = sigs[0];
    const yearList = _formatYearList(sigs.map(s => s.year));

    if (summerStatus && winterStatus && summerStatus === winterStatus) {
      parts.push(`${yearList} Summer and Winter taxes are ${summerStatus}.`);
    } else if (summerStatus && winterStatus) {
      parts.push(`${yearList} Summer taxes are ${summerStatus}.`);
      parts.push(`${yearList} Winter taxes are ${winterStatus}.`);
    } else if (summerStatus) {
      parts.push(`${yearList} Summer taxes are ${summerStatus}.`);
    } else if (winterStatus) {
      parts.push(`${yearList} Winter taxes are ${winterStatus}.`);
    }
    // Feb 2026 — additive Annual sentence; emitted alongside any
    // Summer/Winter content when both seasons coexist on the same
    // year (rare but technically possible).
    if (annualStatus) {
      parts.push(`${yearList} Annual taxes are ${annualStatus}.`);
    }
  }

  // Step 4b — Jul 2026 (NOTES-DATE-DEDUP-001): date clauses are emitted
  // ONCE per season across ALL standard groups, keyed by due month-day.
  // Previously every group re-emitted its own clause, duplicating
  // "Summer taxes were due on 09/14 and became delinquent after 09/15."
  // when e.g. a paid(-late) group and a delinquent group shared the same
  // due dates (TF-20260711-0838). Distinct due dates still get their own
  // clause; DLQ outliers still surface as parenthetical exceptions.
  // Mirrors michigan_tax.py Step 5b.
  for (const [season, statusField, dueField] of [
    ['Summer', 'summerStatus', 'summer_due_md'],
    ['Winter', 'winterStatus', 'winter_due_md'],
    ['Annual', 'annualStatus', 'annual_due_md'],
  ]) {
    const byDue = new Map();
    for (const sigs of groups.values()) {
      for (const s of sigs) {
        if (s[statusField] && s[dueField]) {
          if (!byDue.has(s[dueField])) byDue.set(s[dueField], []);
          byDue.get(s[dueField]).push(s);
        }
      }
    }
    for (const [, sigList] of byDue) {
      const c = _consolidateDateClause(season, sigList, sigList.length > 1);
      if (c) parts.push(c);
    }
  }

  // Step 5 — specialty fallback (delegate to per-year engine, strip preambles)
  for (const sig of specialtySigs) {
    const perYear = getMichiganNotesText(
      [...sig.summer, ...sig.winter],
      sig.year,
      { taxCollected, jurisdictionType, allYearsInstallments },
    );
    if (perYear && perYear[0]) {
      const cleaned = _stripPreambles(perYear[0]);
      if (cleaned) parts.push(cleaned);
    }
  }

  // Step 6 — multi-installment payment-option sentence (once per jurisdiction)
  // NOTES-PAYOPT-DEDUP-001 (Jul 2026): word count comes from the sig that
  // actually HAS the multi-installment pattern (not signatures[0]).
  const multiSig = signatures.find(s => s.summer.length >= 2 && s.winter.length >= 1);
  if (multiSig) {
    let jurLabel = (jurisdictionType || '').trim().toLowerCase();
    jurLabel = ['county', 'city', 'township', 'village', 'borough'].includes(jurLabel)
      ? jurLabel.charAt(0).toUpperCase() + jurLabel.slice(1) : 'jurisdiction';
    const total = multiSig.summer.length + multiSig.winter.length;
    if (total >= 2) {
      parts.push(`This ${jurLabel} offers a ${_numToWord(total)}-installment payment option.`);
    }
  }

  const paragraph = parts.map(p => (p || '').trim()).filter(Boolean).join(' ');
  return paragraph ? [paragraph] : [];
};
